import {
  CURRENCIES,
  DATE_FORMAT_PATTERNS,
  formatBytes as sharedFormatBytes,
  formatMoney as sharedFormatMoney,
  type CurrencyCode,
  type DateFormat,
} from "@l2a/shared";
import { format as dfFormat } from "date-fns";

/**
 * Formatting rules for the whole app (see docs/engineering/frontend-conventions.md §Formatting).
 *
 * Defaults come from the signed-in org (timezone, date format, number locale, base
 * currency) and are set by the AuthProvider through `configureFormatting()`, so plain
 * function calls work anywhere (table column definitions, toasts, exports):
 *   formatMoney(12345600)        → "₹1,23,456.00"   (org base currency, en-IN grouping)
 *   formatMoney(99900, "USD")    → "$999.00"
 *   formatDate("2026-09-18")     → "18/09/2026"      (date-only strings are never TZ-shifted)
 *   formatDateTime(iso)          → "18/09/2026, 3:45 pm" in the org timezone
 *   formatRelative(iso)          → "5m ago", "Yesterday", "in 3 days"
 *   formatDuration(90)           → "1:30"
 */

export interface FormatSettings {
  timezone: string;
  dateFormat: DateFormat;
  /** Number grouping locale (en-IN → 12,34,567.89). */
  locale: string;
  currency: CurrencyCode;
  /** 0 = Sunday … 6 = Saturday. */
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  fyStartMonth: number;
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
}

let settings: FormatSettings = {
  timezone: browserTimeZone(),
  dateFormat: "DD/MM/YYYY",
  locale: "en-IN",
  currency: "INR",
  weekStartsOn: 1,
  fyStartMonth: 4,
};

export function configureFormatting(next: Partial<FormatSettings>): void {
  settings = { ...settings, ...Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined && v !== null)) };
}
export function getFormatSettings(): Readonly<FormatSettings> {
  return settings;
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

export type DateInput = string | number | Date;

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDateOnly(value: unknown): value is string {
  return typeof value === "string" && DATE_ONLY_RE.test(value);
}

/** "2026-09-18" → local Date at midnight (no timezone shift). */
export function parseDateOnly(value: string): Date {
  const m = DATE_ONLY_RE.exec(value);
  if (!m) return new Date(NaN);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Local Date → "YYYY-MM-DD". */
export function toDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = dtfCache.get(timeZone);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      f = new Intl.DateTimeFormat("en-US", { hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    }
    dtfCache.set(timeZone, f);
  }
  return f;
}

function toDate(value: DateInput): Date {
  if (value instanceof Date) return value;
  if (isDateOnly(value)) return parseDateOnly(value);
  return new Date(value);
}

/**
 * The wall-clock time of `value` in `timeZone`, as a local JS Date (for display with
 * date-fns). Never send the result back to the server.
 */
export function toZonedWallClock(value: DateInput, timeZone = settings.timezone): Date {
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return date;
  const parts: Record<string, number> = {};
  for (const p of partsFormatter(timeZone).formatToParts(date)) {
    if (p.type !== "literal") parts[p.type] = Number(p.value);
  }
  return new Date(parts.year ?? 1970, (parts.month ?? 1) - 1, parts.day ?? 1, parts.hour ?? 0, parts.minute ?? 0, parts.second ?? 0);
}

/** Today in the org timezone, "YYYY-MM-DD". */
export function todayISO(timeZone = settings.timezone): string {
  return toDateOnly(toZonedWallClock(new Date(), timeZone));
}

export function datePattern(dateFormat: DateFormat = settings.dateFormat): string {
  return DATE_FORMAT_PATTERNS[dateFormat] ?? "dd/MM/yyyy";
}

function wall(value: DateInput, timeZone?: string): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = isDateOnly(value) ? parseDateOnly(value) : toZonedWallClock(value, timeZone);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Org date format, e.g. "18/09/2026". Date-only strings are not timezone-shifted. */
export function formatDate(value: DateInput | null | undefined, opts: { pattern?: string; timeZone?: string } = {}): string {
  const d = value === null || value === undefined ? null : wall(value, opts.timeZone);
  if (!d) return "—";
  return dfFormat(d, opts.pattern ?? datePattern());
}

/** Compact date for dense UI: "18 Sep" (this year) or "18 Sep 2025". */
export function formatDateShort(value: DateInput | null | undefined): string {
  const d = value === null || value === undefined ? null : wall(value);
  if (!d) return "—";
  const thisYear = toZonedWallClock(new Date()).getFullYear() === d.getFullYear();
  return dfFormat(d, thisYear ? "d MMM" : "d MMM yyyy");
}

export function formatTime(value: DateInput | null | undefined, opts: { timeZone?: string } = {}): string {
  const d = value === null || value === undefined ? null : wall(value, opts.timeZone);
  if (!d) return "—";
  return dfFormat(d, "h:mm aaa");
}

/** "18/09/2026, 3:45 pm" in the org timezone. */
export function formatDateTime(value: DateInput | null | undefined, opts: { timeZone?: string } = {}): string {
  const d = value === null || value === undefined ? null : wall(value, opts.timeZone);
  if (!d) return "—";
  return `${dfFormat(d, datePattern())}, ${dfFormat(d, "h:mm aaa")}`;
}

const rtf = typeof Intl !== "undefined" && "RelativeTimeFormat" in Intl ? new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "short" }) : null;

/** "just now", "5m ago", "3h ago", "Yesterday", "Mon", "in 2 days", else the org date. */
export function formatRelative(value: DateInput | null | undefined, now: Date = new Date()): string {
  if (value === null || value === undefined || value === "") return "—";
  const date = toDate(value);
  const diffMs = date.getTime() - now.getTime();
  if (Number.isNaN(diffMs)) return "—";
  const abs = Math.abs(diffMs);
  const future = diffMs > 0;
  const min = 60_000;
  const hour = 60 * min;
  if (abs < 45_000) return future ? "in a moment" : "just now";
  if (abs < hour) {
    const m = Math.round(abs / min);
    return future ? `in ${m}m` : `${m}m ago`;
  }
  const a = toZonedWallClock(date);
  const b = toZonedWallClock(now);
  const dayDiff = Math.round((new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime() - new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime()) / 86_400_000);
  if (dayDiff === 0) {
    const h = Math.round(abs / hour);
    return future ? `in ${h}h` : `${h}h ago`;
  }
  if (Math.abs(dayDiff) === 1) return rtf ? capitalize(rtf.format(dayDiff, "day")) : dayDiff < 0 ? "Yesterday" : "Tomorrow";
  if (dayDiff < 0 && dayDiff > -7) return dfFormat(a, "EEE");
  if (dayDiff > 0 && dayDiff < 14) return `in ${dayDiff} days`;
  return formatDateShort(date);
}

/** Full sentence form for tooltips: "Friday, 18 September 2026 at 3:45 pm". */
export function formatDateTimeLong(value: DateInput | null | undefined): string {
  const d = value === null || value === undefined ? null : wall(value);
  if (!d) return "—";
  return dfFormat(d, "EEEE, d MMMM yyyy 'at' h:mm aaa");
}

function capitalize(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

// ---------------------------------------------------------------------------
// Numbers & money
// ---------------------------------------------------------------------------

/** Money from integer minor units. Defaults to the org base currency and number locale. */
export function formatMoney(
  minor: number | null | undefined,
  currency: CurrencyCode = settings.currency,
  opts: { compact?: boolean; hideZeroDecimals?: boolean; locale?: string; signDisplay?: "auto" | "always" } = {},
): string {
  if (minor === null || minor === undefined || Number.isNaN(minor)) return "—";
  const locale = opts.locale ?? (currency === "INR" ? "en-IN" : settings.locale === "en-IN" ? CURRENCIES[currency]?.locale ?? "en-US" : settings.locale);
  const text = sharedFormatMoney(minor, currency, { compact: opts.compact, hideZeroDecimals: opts.hideZeroDecimals, locale });
  return opts.signDisplay === "always" && minor > 0 ? `+${text}` : text;
}

export function formatNumber(value: number | null | undefined, opts: Intl.NumberFormatOptions = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(settings.locale, { maximumFractionDigits: 2, ...opts }).format(value);
}

/** 1800 bps → "18%"; 12.5 → "12.5%" with `{ unit: "percent" }`. */
export function formatPercent(value: number | null | undefined, opts: { unit?: "bps" | "percent" | "fraction"; digits?: number; signDisplay?: "auto" | "always" } = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const unit = opts.unit ?? "percent";
  const fraction = unit === "bps" ? value / 10_000 : unit === "percent" ? value / 100 : value;
  return new Intl.NumberFormat(settings.locale, {
    style: "percent",
    maximumFractionDigits: opts.digits ?? 1,
    signDisplay: opts.signDisplay === "always" ? "exceptZero" : "auto",
  }).format(fraction);
}

/** Minutes → "h:mm" (timesheets). */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return "—";
  const sign = minutes < 0 ? "-" : "";
  const abs = Math.round(Math.abs(minutes));
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}

/** Minutes → "1h 30m" / "45m" / "2h". */
export function formatDurationWords(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return "—";
  const abs = Math.round(Math.abs(minutes));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const text = h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
  return minutes < 0 ? `-${text}` : text;
}

export const formatBytes = sharedFormatBytes;

/** "Priya Sharma" → "PS". */
export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/[\s._@-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : (parts[0]![1] ?? "");
  return (first + last).toUpperCase();
}

/** "1 client" / "3 clients". */
export function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return `${formatNumber(count)} ${count === 1 ? singular : pluralForm}`;
}
